import type { PaletteMode, PaletteOptions, Theme } from "@mui/material";
import { createTheme, lighten, type ThemeOptions } from "@mui/material/styles";
import { csCZ } from "@mui/x-date-pickers/locales";
import type { GetThemeFunctionType } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import {
	Colors,
	DarkModeColors,
	type HspThemeColors,
} from "web_myplann/Infrastructure/Themes/Colors";
import "@mui/x-date-pickers/themeAugmentation";

const theme = (color: HspThemeColors, variant: PaletteMode): Theme => {
	const palette = {
		mode: variant,
		primary: {
			light: color.hspColor25 || color.Primary25,
			main: color.hspColor || color.Primary,
			dark: color.hspColor75 || color.Primary75,
			contrastText: color.White,
		},
		secondary: {
			light: color.Secondary25,
			main: color.Secondary,
			dark: color.Secondary75,
			contrastText: color.Black,
		},
		text: {
			primary: color.Black,
			secondary: color.MonoChrome500,
			disabled: color.MonoChrome400,
		},
		error: {
			main: color.Danger,
			light: color.Danger50,
			dark: color.Danger75,
			contrastText: color.White,
		},
		success: {
			main: color.Success,
			contrastText: color.White,
		},
		info: {
			main: color.Secondary,
			light: color.Secondary50,
			dark: color.Secondary50,
			contrastText: color.White,
		},
		background: {
			default: color.White,
		},
		common: {
			black: color.Black,
			white: color.White,
		},
		warning: {
			main: color.Warning,
			light: color.Warning25,
		},
		grey: {
			50: color.MonoChrome50,
			100: color.MonoChrome100,
			200: color.MonoChrome200,
			300: color.MonoChrome300,
			400: color.MonoChrome400,
			500: color.MonoChrome500,
			600: color.MonoChrome600,
			700: color.MonoChrome700,
			800: color.MonoChrome800,
			900: color.Black,
		},
		comodities: {
			light: color.Comodities25,
			main: color.Comodities,
			dark: color.Comodities75,
			contrastText: color.White,
		},
		blue: {
			light: color.Blue25,
			main: color.Blue,
			dark: color.Blue75,
			contrastText: color.White,
		},
		pink: {
			light: color.Pink25,
			main: color.Pink,
			dark: color.Pink75,
			contrastText: color.White,
		},
		orange: {
			light: color.Orange25,
			main: color.Orange,
			dark: color.Orange75,
			contrastText: color.White,
		},
		purple: {
			light: color.Purple25,
			main: color.Purple,
			dark: color.Purple75,
			contrastText: color.White,
		},
		magenta: {
			light: color.Magenta25,
			main: color.Magenta,
			dark: color.Magenta75,
			contrastText: color.White,
		},
		greyHalf: {
			main: color.Grey75,
		},
		greyLight: {
			main: color.Grey25,
		},
		paidAdvisory: {
			main: color.PaidAdvisory,
		},
		paper: {
			main: color.Paper,
		},
		dismiss: {
			light: color.MonoChrome50,
			main: color.MonoChrome500,
			dark: color.MonoChrome500,
			contrastText: color.MonoChrome500,
		},
		default: {
			light: color.MonoChrome200,
			main: color.MonoChrome300,
			dark: color.MonoChrome400,
			contrastText: color.Black,
		},
		primaryGreen: {
			light: color.Primary25,
			main: color.Primary,
			dark: color.Primary75,
			contrastText: color.White,
		},
		myPLANN: {
			main: color.Secondary,
			main75: color.Secondary75,
			main50: color.Secondary50,
			main25: color.Secondary25,
			main10: color.Secondary10,
		},
		divider: color.MonoChrome300,
		darkmodeText: {
			white: variant === "light" ? color.White : color.Black,
			black: variant === "light" ? color.Black : color.White,
		},
		darkGrey: {
			main: color.DarkGrey,
		},
	} as const satisfies PaletteOptions;
	const theme: ThemeOptions = {
		palette,
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 1050,
				lg: 1200,
				xl: 1536,
			},
		},
		spacing: 10,
		shape: {
			borderRadius: 4,
		},
		typography: {
			fontSize: 14,
			fontFamily: "'Inter', sans-serif",
			body1: {
				fontSize: 14,
				fontWeight: 400,
				letterSpacing: "0.2px",
				bold: {
					fontWeight: 600,
				},
			},
			body2: {
				color: color.MonoChrome400,
				fontWeight: 600,
				fontSize: 14,
				letterSpacing: "0.2px",
			},
			h1: {
				color: color.Black,
				fontWeight: 400,
				fontSize: 32,
				lineHeight: "35px",
			},
			h2: {
				color: color.Black,
				fontWeight: 400,
				fontSize: 24,
				lineHeight: "26px",
				letterSpacing: "0.2px",
			},
			h3: {
				color: color.Black,
				fontWeight: 600,
				fontSize: 16,
				lineHeight: "17px",
				letterSpacing: "0.2px",
			},
			h4: {
				color: color.Black,
				fontWeight: 700,
				fontSize: 24,
			},
			subtitle3: {
				fontWeight: 700,
				fontSize: 16,
			},
			subtitle1: {
				fontWeight: 400,
				color: color.Black,
				fontSize: 16,
				lineHeight: 1.4,
				letterSpacing: "0.2px",
			},
			subtitle2: {
				fontWeight: 300,
				color: color.MonoChrome400,
				fontSize: 10,
				fontStyle: "italic",
				lineHeight: "14px",
				letterSpacing: "0.2px",
			},
			button: {
				fontSize: 12,
				fontWeight: 500,
			},
			caption: {
				fontSize: "12px",
				fontWeight: 400,
				letterSpacing: "0.2px",
				lineHeight: "15.6px",
			},
			filter: {
				fontSize: "16px",
				fontWeight: 500,
				letterSpacing: "0.2px",
				lineHeight: "22.4px",
			},
		},
		components: {
			MuiButtonBase: {
				defaultProps: {
					// The props to apply
					disableRipple: false, // let the ripple live again 💣!
				},
				styleOverrides: {
					root: {
						"&.MuiButton-sizeLarge": {
							fontSize: "16px",
							fontWeight: 700,
							padding: "14px 91px",
							height: "auto",
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					html: {
						height: "100%",
						position: "relative",
						backgroundColor: color.White,
						overscrollBehavior: "none",
					},
					body: {
						backgroundColor: color.White,
						height: "100%",
						overflow: "auto",
						position: "relative",
						color: color.Black,
						overscrollBehavior: "none",
						fontWeight: 400,
					},
					root: {
						overscrollBehavior: "none",
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						fontSize: "12px",
						color: color.Secondary,
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						boxShadow: "0 2px 6px 0 rgba(215, 215, 215, 0.5)",
						"& .MuiPickersBasePicker-container .MuiPickerDTTabs-tabs .MuiButtonBase-root":
							{
								color: color.White,
							},
					},
				},
			},
			MuiButton: {
				defaultProps: {
					variant: "contained",
				},
				styleOverrides: {
					root: {
						textTransform: "none",
						borderRadius: "28px",
						fontSize: "16px",
						fontWeight: "700",
						boxShadow: "none",
						"&:hover": {
							boxShadow: "none",
						},
						"&.Mui-disabled": {
							backgroundColor:
								variant === "dark" ? palette.grey[500] : palette.grey[300],
						},
					},
					sizeMedium: {
						height: "56px",
					},
					text: {
						border: "none",
						"&:hover": {
							boxShadow: "none",
							border: "none",
						},
					},
					contained: {
						color:
							variant === "dark" ? palette.common.black : palette.common.white,
					},
				},
			},
			MuiDivider: {
				styleOverrides: {
					root: {
						"&::before, &::after": {
							borderColor: color.MonoChrome100,
						},
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						lineHeight: "1.9em",
					},
					shrink: {
						pointerEvents: "none",
						fontWeight: 400,
						fontSize: "12px",
						color: color.MonoChrome400,
						padding: 0,
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						fontWeight: 500,
						fontSize: "14px",
						padding: "8px 12px",
						border: `1px solid ${palette.grey[300]}`,
						borderRadius: "12px",

						"& .MuiOutlinedInput-notchedOutline": {
							border: "none",
						},
						":not(MuiSvgIcon-root)+svg:first-of-type": {
							order: 0,
						},
						"*:nth-last-child(2)": {
							":not(.MuiChip-label):not(.MuiChip-root)": {
								order: 3,
							},
						},
					},
					input: {
						padding: 0,
					},
					notchedOutline: {},
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						borderRadius: "0 0 5px 5px",
						overflow: "scroll",
						background: "white",
						boxShadow: "inset 0 -10px 0 1px #CBD5E1",
						transform: "translateY(-4px) !important",
					},
					list: {
						backgroundColor: color.MonoChrome50,
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						background: color.MonoChrome50,
						hover: {
							background: color.MonoChrome300,
						},
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					root: {
						width: "95%",
						margin: "0 auto",
						borderBottom: `1px solid ${color.MonoChrome100}`,
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						flexGrow: 1,
						textAlign: "center",
						color: color.Black,
						"&.Mui-selected": {
							color: color.Black,
							fontWeight: 700,
						},
					},
				},
			},
			MuiSwitch: {
				defaultProps: {
					disableRipple: true,
				},
				styleOverrides: {
					root: {
						width: 48,
						height: 26,
						padding: 0,
						"& .MuiSwitch-switchBase": {
							padding: 0,
							margin: 2,
							transitionDuration: "300ms",
							"&.Mui-checked": {
								transform: "translateX(22px)",
								"& + .MuiSwitch-track": {
									backgroundColor: palette.primary.main,
									opacity: 1,
									border: 0,
								},
								"&.Mui-disabled + .MuiSwitch-track": {
									opacity: 0.5,
								},
							},
							"&.Mui-disabled .MuiSwitch-thumb": {
								color: palette.primary.main,
							},
							"&.Mui-disabled + .MuiSwitch-track": {
								opacity: 1,
							},
						},
						"& .MuiSwitch-thumb": {
							boxSizing: "border-box",
							width: 22,
							height: 22,
							backgroundColor: color.White,
						},
						"& .MuiSwitch-track": {
							borderRadius: 26 / 2,
							backgroundColor: color.MonoChrome300,
							opacity: 1,
						},
					},
				},
			},
			MuiRadio: {
				styleOverrides: {},
			},
			MuiFormControlLabel: {
				styleOverrides: {
					root: {
						paddingInline: "16px",
						margin: "0px",
						flexDirection: "row-reverse",
						width: "100%",
						justifyContent: "space-between",
						borderBottom: "none",
						"&:not(:last-of-type)": {
							borderBottom: `1px solid ${color.MonoChrome100}`,
						},
						"&:last-of-type": {
							paddingBottom: "1px",
						},
					},
					label: {
						fontSize: "15px",
						fontWeight: 400,
						paddingTop: "12px",
						paddingBottom: "12px",
					},
				},
			},
			MuiCheckbox: {
				defaultProps: {
					size: "small",
				},
				styleOverrides: {
					root: {
						"&.MuiCheckbox-sizeLarge": {
							"& svg": {
								transform: "scale(1.5)",
							},
						},
						"&.MuiCheckbox-sizeMedium": {
							"& svg": {
								transform: "scale(1.25)",
							},
						},
						"&.Mui-disabled": {
							opacity: 0.5,
							"&.MuiCheckbox-colorPrimary": {
								color: palette.primary.main,
							},
							"&.MuiCheckbox-colorSecondary": {
								color: color.Secondary,
							},
						},
					},
				},
			},
			MuiPagination: {
				styleOverrides: {
					root: {
						border: `1px solid ${color.MonoChrome300}`,
						borderRadius: "50px",
						"& .MuiPaginationItem-root": {
							color: color.MonoChrome400,
							"&.Mui-selected": {
								color: palette.primary.main,
								backgroundColor: "transparent",
							},
						},
					},
				},
			},
			MuiPaginationItem: {
				styleOverrides: {
					root: {
						padding: "0px",
						margin: "0px",
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					root: {
						"& .MuiPaper-root": {
							border: `1px solid ${color.MonoChrome300}`,
							borderRadius: 12,
							boxShadow: "none",
							backgroundColor: color.White,
							backgroundImage: "none",
						},
					},
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						borderTopLeftRadius: "16px",
						borderTopRightRadius: "16px",
						maxHeight: "80%",
						backgroundColor: color.MonoChrome50,
						backgroundImage: "none",
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: {
						backgroundColor: color.White,
					},
					root: {
						"& .MuiOutlinedInput-root": {
							padding: "0px 0px 0px 12px",
						},
					},
					inputRoot: {
						"&.MuiInputBase-root": {
							borderRadius: "12px",
							backgroundColor: color.White,
						},
						"& .MuiOutlinedInput-input": {
							padding: "14px 12px",
							textAlign: "left",
							direction: "ltr",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderRadius: "12px",
						},
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					root: {},
					paper: {
						boxShadow: "none",
						backgroundColor: color.White,
						borderRadius: "32px",
						padding: "0px",
						width: "60%",
						"@media (max-width: 900px)": {
							width: "90%",
							margin: "16px",
						},
					},
				},
			},
			MuiDialogContent: {
				defaultProps: {
					dividers: false,
				},
				styleOverrides: {
					root: {
						position: "relative",
						padding: "12px 16px",
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						padding: "16px",
						paddingTop: "24px",
						fontSize: "16px",
						fontWeight: 500,
						lineHeight: "22.4px",
						gap: "16px",
						display: "flex",
						alignItems: "center",
					},
				},
			},
			MuiDialogActions: {
				styleOverrides: {
					root: {
						padding: "0 16px 24px 16px",
					},
				},
			},
			MuiToolbar: {
				styleOverrides: {
					root: {
						"&.MuiToolbar-gutters": {
							padding: "0px",
							minHeight: "0px",
						},
					},
				},
			},
			// @ts-expect-error-next-line TODO Fix later
			MuiCalendarPicker: {
				styleOverrides: {
					root: {
						margin: "0",
					},
				},
			},
			MuiPickersLayout: {
				styleOverrides: {
					contentWrapper: {
						alignItems: "center",
					},
				},
			},
		},
		colors: {
			border: {
				main: "",
				hover: "",
				active: "",
			},
			placeholder: "",
		},
		shadow: {
			primary: "",
			primaryHover: "",
		},
		transition: {
			fast: "",
			slow: "",
		},
		borderRadius: "",
		vh: (_value: number): string => {
			throw new Error("Function not implemented.");
		},
		timeline: {
			height: {
				small: "",
				medium: "",
				large: "",
			},
		},
	};

	const customVariables = {
		shape: {},
		colors: {
			placeholder: color.MonoChrome400,
			border: {
				main: color.MonoChrome300,
				hover: color.MonoChrome400,
				active: palette.primary.main,
			},
		},
		shadow: {
			primary: "none",
			primaryHover: "0px 0px 15px 0px rgba(0, 0, 0, 0.15) inset",
		},
		borderRadius: "5px",
		transition: {
			fast: "0.2s ease",
			slow: "0.45s ease",
		},
		vh: (val: number) => {
			return `calc(var(--vh, 1vh) * ${val})`;
		},
	};

	// TODO: fix later, probably theme missing some properties
	return createTheme(
		Object.assign(theme, customVariables) as any,
		csCZ,
	) as Theme;
};

export const getMyPlannTheme: GetThemeFunctionType = (variant, hspColor) => {
	const colorPalette = variant === "dark" ? DarkModeColors : Colors;
	const hspPalette: HspThemeColors = hspColor
		? {
				...colorPalette,
				hspColor: hspColor,
				hspColor75: lighten(hspColor, 0.25),
				hspColor25: lighten(hspColor, 0.75),
			}
		: colorPalette;
	return theme(hspPalette, variant);
};
